// noinspection ES6UnusedImports
import React, { useEffect, useRef, useState } from 'react';

import { Typography } from '@bonprix/design-system';
import Breakpoint from '@core/scripts/helper/breakpoint.js';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import SymbolButton from '@bonprix/pattern-library/templates/symbol-button/default';
import Device from '@core/scripts/components/device';
import Messages from '@core/scripts/components/messages';
import onceVisible from '@core/scripts/helper/once-visible';

const ScrollTrigger = {
    SCROLLING: 'SCROLLING',
    BULLET: 'BULLET',
    BUTTON: 'BUTTON',
};

const SlideWidth = {
    small: 4,
    medium: 4,
    large: 3,
    xlarge: 2,
    max: 2,
};

export default function Slider({
    id,
    className = 'recommendations',
    title,
    items,
    imagesOnly,
    onIsVisible,
    onSlideChanged,
    optimizeButtonHandling,
    children,
    autoButtonPositioning,
    sendVisibleLandmarkOnItemsChange,
}) {
    const [activeBulletIndex, setActiveBulletIndex] = useState(0);
    const [slideGroup, setSlideGroup] = useState(calculateSlideGroups);
    const [buttonTop, setButtonTop] = useState(0);

    const sliderRef = useRef(null);
    const nextButtonRef = useRef();
    const scrolledBy = useRef({trigger: ScrollTrigger.SCROLLING});
    const scrollingTimer = useRef(0);
    const isInitial = useRef(true);

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, () => {
                setSlideGroup(calculateSlideGroups());
            }),
            MessageBroker.subscribe(Messages.WINDOW.RESIZED, () => {
                if (autoButtonPositioning) {
                    calculateButtonsTopPosition();
                }
            }),
        ];

        if (id && items) {
            const slideToSubscription = SliderAction.registerSlideToEvent(id, (slideIndex) => {
                const previousSlideIndex = activeBulletIndex;
                const newBulletIndex = (slideIndex % items.length) / slideGroup;
                setActiveBulletIndex(newBulletIndex);

                if ((Device.isTouch() || Device.isApp()) && previousSlideIndex === slideIndex) {
                    const {position} = computeNextPosition();
                    sliderRef.current?.scroll({
                        left: position,
                        behavior: 'smooth',
                    });
                }
            });
            subscriptions.push(slideToSubscription);
        }

        if (items && sliderRef?.current) {
            onceVisible(
                sliderRef.current,
                () => {
                    onIsVisible?.({items, displayedItemCount: slideGroup});
                },
                1
            );
        }

        if (autoButtonPositioning) {
            calculateButtonsTopPosition();
        }

        return () => subscriptions.forEach((subscription) => MessageBroker.unsubscribe(subscription));
    }, []);

    useEffect(() => {
        const {position} = computeNextPosition();
        sliderRef.current?.scroll({
            left: position,
            behavior: 'smooth',
        });
    }, [activeBulletIndex]);

    useEffect(() => {
        if (sendVisibleLandmarkOnItemsChange) {
            if (isInitial.current) {
                isInitial.current = false;
            } else {
                onIsVisible?.({items, displayedItemCount: slideGroup});
            }
        }
    }, [items]);

    function computeNextPosition() {
        let position = 0;
        let newSlideNode;
        let newSlideIndex;

        if (items?.length > slideGroup && sliderRef && activeBulletIndex !== 0) {
            newSlideIndex = activeBulletIndex * slideGroup;
            const lastVisibleSlideIndex = newSlideIndex + slideGroup;

            if (lastVisibleSlideIndex >= items.length) {
                newSlideIndex = items.length - slideGroup;
            }
            newSlideNode = sliderRef.current.children[newSlideIndex];
            const offsetFirstChild = getComputedStyle(sliderRef.current.children[0])?.marginLeft;
            position = newSlideNode.offsetLeft - (offsetFirstChild ? parseInt(offsetFirstChild) : 0);
        }

        return {position};
    }

    function onSliderButtonClick(isSlideForward) {
        scrolledBy.current = {trigger: ScrollTrigger.BUTTON};
        if (optimizeButtonHandling) {
            setTimeout(() => updateBulletIndex(isSlideForward), 20);
            return;
        }
        updateBulletIndex(isSlideForward);
    }

    function updateBulletIndex(isSlideForward) {
        isSlideForward
            ? setActiveBulletIndex(Math.min(activeBulletIndex + 1, maxBulletCount))
            : setActiveBulletIndex(Math.max(activeBulletIndex - 1, 0));
    }

    function onScroll() {
        //Workaround for detecting if scrolling has been completed
        clearTimeout(scrollingTimer.current);
        scrollingTimer.current = setTimeout(() => {
            //this part is called only if the last event was more than
            //the chosen timeout duration ago
            if (scrolledBy.current.trigger !== ScrollTrigger.BULLET) {
                onSlideChanged?.({items, startIndex: activeBulletIndex * slideGroup, displayedItemCount: slideGroup});
            }
            scrolledBy.current = {trigger: ScrollTrigger.SCROLLING};
        }, 50); //this is an arbitrary duration for the timeout
    }

    function onTouchEnd() {
        scrolledBy.current = {trigger: ScrollTrigger.SCROLLING};
    }

    function calculateSlideGroups() {
        switch (Breakpoint.current().key) {
            case 'small':
                return imagesOnly ? 3 : 2;
            case 'medium':
                return imagesOnly ? 5 : 2;
            case 'large':
                return imagesOnly ? 5 : 3;
            case 'xlarge':
                return imagesOnly ? 6 : 4;
            case 'max':
                return imagesOnly ? 6 : 4;
            default:
                return imagesOnly ? 7 : 5;
        }
    }

    function calculateButtonsTopPosition() {
        const percentageWidth = (1 / 12) * SlideWidth[Breakpoint.current().key];
        const heightFactor = 1.4;
        const imageHeight = sliderRef.current?.offsetWidth * percentageWidth * heightFactor;
        const buttonHeight = 60;
        setButtonTop(imageHeight / 2 - buttonHeight / 2);
    }

    const isTouch = Device.isTouch();
    const itemCount = items?.length;
    const maxBulletCount = Math.ceil(itemCount / slideGroup);

    return (
        <>
            {items?.length > 0 && (
                <>
                    <div className={`${className}__headline`}>
                        <Typography variant="bonprix-h4" component="span">
                            {title}
                        </Typography>
                    </div>
                    <div className={`${className}__slider`} test-ref__reco-slider={id}>
                        <div
                            ref={sliderRef}
                            className={`${className}__slider-content`}
                            onScroll={onScroll}
                            onTouchEnd={onTouchEnd}
                        >
                            {children}
                        </div>
                        {activeBulletIndex !== 0 && !isTouch && (
                            <SymbolButton
                                attributes={buttonTop ? {style: {top: buttonTop}} : null}
                                onClick={() => onSliderButtonClick(false)}
                                className={`${className}__slider-button prev`}
                                icon="arrow-left"
                            />
                        )}

                        {activeBulletIndex !== maxBulletCount - 1 && !isTouch ? (
                            <SymbolButton
                                attributes={buttonTop ? {style: {top: buttonTop}} : null}
                                ref={nextButtonRef}
                                onClick={() => onSliderButtonClick(true)}
                                className={`${className}__slider-button next`}
                                icon="arrow-right"
                            />
                        ) : null}
                    </div>
                </>
            )}
        </>
    );
}

export const SliderAction = {
    Type: {
        SLIDE_TO: 'SLIDE_TO',
    },
    changeSlideTo: (event) => {
        MessageBroker.publish(`${SliderAction.Type.SLIDE_TO}:${event.sliderId}`, event.slideIndex);
    },
    registerSlideToEvent: (id, slideToCallback) => {
        return MessageBroker.subscribe(`${SliderAction.Type.SLIDE_TO}:${id}`, (slideIndex) => {
            slideToCallback?.(slideIndex);
        });
    },
};
